import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import './admin-panel.css';

const emptyForm = {
  title: '',
  category: '',
  excerpt: '',
  content: '',
};

export default function CreateBlogTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const editId = location.state?.editId;

  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null); // { file, previewUrl }
  const [existingImage, setExistingImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editId));

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;

    axios
      .get('/api/blogs/', { params: { status: 'all' } })
      .then((response) => {
        if (cancelled) return;
        const blog = (response.data?.blogs ?? []).find((b) => b.id === editId);
        if (!blog) {
          toast.error('Blog not found.');
          return;
        }
        setForm({
          title: blog.title || '',
          category: blog.category || '',
          excerpt: blog.excerpt || '',
          content: blog.content || '',
        });
        setExistingImage(blog.image || null);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load the blog for editing.');
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editId]);

  useEffect(() => {
    return () => {
      if (image) URL.revokeObjectURL(image.previewUrl);
    };
  }, [image]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (image) URL.revokeObjectURL(image.previewUrl);
    setImage({ file, previewUrl: URL.createObjectURL(file) });
  };

  const removeImage = () => {
    if (image) URL.revokeObjectURL(image.previewUrl);
    setImage(null);
  };

  const resetForm = () => {
    setForm(emptyForm);
    if (image) URL.revokeObjectURL(image.previewUrl);
    setImage(null);
    setExistingImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim() || !form.category.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    if (editId) {
      // Editing only updates text fields today — the cover image can't be
      // changed on an existing blog until the API supports it.
      setSubmitting(true);
      try {
        await axios.post(`/api/blogs/?id=${editId}&_method=PATCH`, {
          ...form,
          status: 'review',
        });
        toast.success('Blog updated and sent back for review.');
        navigate('/adminpanel/blogs/review');
      } catch {
        toast.error('Could not update the blog. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!image) {
      toast.error('Please add a cover image.');
      return;
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    payload.append('image', image.file);

    setSubmitting(true);
    try {
      await axios.post('/api/blogs/', payload);
      toast.success('Blog submitted for review.');
      resetForm();
    } catch (err) {
      const message = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : 'Could not submit the blog. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div>
        <div className="admin-topbar">
          <div>
            <h1>Edit Blog</h1>
            <p>Loading blog…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-topbar">
        <div>
          <h1>{editId ? 'Edit Blog' : 'Create Blog'}</h1>
          <p>
            {editId
              ? 'Update the blog details below. Saving will send it back to Review.'
              : 'Fill in the blog details. New blogs are sent to the Review tab.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="admin-panel">
          <h3 className="admin-panel-title">
            <i className="fa fa-info-circle" /> Basic Details
          </h3>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Blog Title</label>
              <input
                type="text"
                placeholder="Blog Title"
                value={form.title}
                onChange={handleChange('title')}
                required
              />
            </div>
            <div className="admin-field">
              <label>Category</label>
              <input
                type="text"
                placeholder="e.g. Destinations"
                value={form.category}
                onChange={handleChange('category')}
                required
              />
            </div>
          </div>
          <div className="admin-field">
            <label>Excerpt</label>
            <textarea
              rows={2}
              placeholder="Short summary shown on the blog card..."
              value={form.excerpt}
              onChange={handleChange('excerpt')}
              required
            />
          </div>
          <div className="admin-field">
            <label>Content</label>
            <textarea
              rows={10}
              placeholder="Write the full blog content here. Separate paragraphs with a blank line."
              value={form.content}
              onChange={handleChange('content')}
              required
            />
          </div>
        </div>

        <div className="admin-panel">
          <h3 className="admin-panel-title">
            <i className="fa fa-image" /> Cover Image
          </h3>
          <p className="admin-field-hint">
            {editId
              ? 'The cover image cannot be changed while editing yet.'
              : 'Add one cover image (JPG or PNG, up to 5MB).'}
          </p>

          {existingImage && (
            <div className="admin-image-thumb-grid">
              <div className="admin-image-thumb">
                <img src={existingImage} alt="Cover" />
                <span className="admin-image-thumb-cover">Cover</span>
              </div>
            </div>
          )}

          {!editId && image && (
            <div className="admin-image-thumb-grid">
              <div className="admin-image-thumb">
                <img src={image.previewUrl} alt="Cover" />
                <span className="admin-image-thumb-cover">Cover</span>
                <button
                  type="button"
                  className="admin-image-thumb-remove"
                  onClick={removeImage}
                  title="Remove image"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>
          )}

          {!editId && !image && (
            <label className="admin-image-drop admin-image-drop-compact">
              <div className="admin-image-drop-icon">
                <i className="fa fa-cloud-upload-alt" />
              </div>
              <div>Click to upload a cover image</div>
              <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 4 }}>
                JPG or PNG
              </div>
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </label>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting}>
            <i className="fa fa-paper-plane" /> {editId ? 'Save Changes' : 'Submit for Review'}
          </button>
          <button type="button" className="admin-btn admin-btn-outline" onClick={resetForm} disabled={submitting}>
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}
